import { z } from 'zod';
import { fromCents, toCents } from './money.js';
import { parseIsoDate } from './dates.js';
import { idSchema, isoDateSchema, optionalText } from './validation.js';
import { FATTURA_XML_MAX_LENGTH } from './fattura-pa.js';
/** Issued to clients (ciclo attivo) or received from suppliers (ciclo passivo). */
export const INVOICE_DIRECTIONS = ['emessa', 'ricevuta'];
export const INVOICE_DIRECTION_LABELS = {
    emessa: 'Emesse',
    ricevuta: 'Ricevute',
};
/**
 * Where an invoice comes from: Gestify, the situation at 1 January fixed from
 * the ledgers (09/10/2026), an item still open then, or entered by hand (1.59):
 * an invoice that never reaches Gestify (a private person) or a pro forma.
 */
export const INVOICE_ORIGINS = ['gestify', 'apertura', 'manuale'];
/** The documents of Gestify. */
export const GESTIFY_INVOICE_KINDS = ['fattura', 'nota_credito'];
/**
 * A pro forma (1.59) is a debt entered by hand, replaced later by the
 * invoices of Gestify: never a cost of the year.
 */
export const INVOICE_KINDS = [...GESTIFY_INVOICE_KINDS, 'proforma'];
export const INVOICE_KIND_LABELS = {
    fattura: 'Fattura',
    nota_credito: 'Nota di credito',
    proforma: 'Pro-forma',
};
/** The first day read from Gestify (06/10/2026: 2025; 09/10/2026: 2024, to reconcile the movements of 2024). */
export const TREASURY_START_DATE = '2024-01-01';
export const INVOICE_STATES = ['aperta', 'parziale', 'scaduta', 'saldata'];
/**
 * The words change with the direction: an issued invoice is collected, a
 * received one paid. A credit note is used by linking it to invoices or by a
 * refund (1.58).
 */
export function invoiceStateLabel(state, direction, kind = 'fattura') {
    if (kind === 'nota_credito') {
        if (state === 'saldata')
            return 'Chiusa';
        return state === 'parziale' ? 'Usata in parte' : 'Da usare';
    }
    const issued = direction === 'emessa';
    switch (state) {
        case 'aperta':
            return issued ? 'Da incassare' : 'Da pagare';
        case 'parziale':
            return issued ? 'Incassata in parte' : 'Pagata in parte';
        case 'scaduta':
            return 'Scaduta';
        case 'saldata':
            return issued ? 'Incassata' : 'Pagata';
    }
}
/** What is actually collected or paid: the total less the VAT in split payment and the withholding. */
export function invoiceNetDue(amounts) {
    const cents = toCents(amounts.total) -
        (amounts.splitPayment ? toCents(amounts.vat) : 0) -
        toCents(amounts.withholding);
    return fromCents(cents);
}
const DAY = 86_400_000;
/**
 * Where an invoice stands: the collections or payments cover the due dates in
 * order, the oldest first; without due dates from Gestify the whole amount is
 * due on the date of the invoice. Credit notes work the same, with negative
 * amounts. Due dates that do not add up to what is due are shown as they are,
 * with the difference (07/10/2026): never an invented due date.
 */
export function invoiceStatus(input) {
    const net = toCents(input.netDue);
    const sign = net < 0 ? -1 : 1;
    const settledCents = input.settlements.reduce((sum, item) => sum + toCents(item.amount), 0);
    const dues = [
        ...(input.dues.length ? input.dues : [{ dueDate: input.date, amount: input.netDue }]),
    ].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    // Gestify gives the due dates of a credit note as positive (1.83): they take the sign of the note.
    const amounts = dues.map((due) => {
        const cents = toCents(due.amount);
        return net < 0 && cents > 0 ? -cents : cents;
    });
    const duesTotal = amounts.reduce((sum, cents) => sum + cents, 0);
    let available = settledCents * sign;
    const today = parseIsoDate(input.today).getTime();
    let overdue = 0;
    let overdueDays = 0;
    let nextDue = null;
    const statuses = dues.map((due, index) => {
        const amount = amounts[index] * sign;
        const covered = Math.max(0, Math.min(amount, available));
        available -= covered;
        const residual = amount - covered;
        const late = Math.floor((today - parseIsoDate(due.dueDate).getTime()) / DAY);
        const days = residual > 0 && late > 0 ? late : 0;
        if (residual > 0 && nextDue === null)
            nextDue = due.dueDate;
        if (days > 0) {
            overdue += residual;
            overdueDays = Math.max(overdueDays, days);
        }
        return {
            dueDate: due.dueDate,
            amount: fromCents(amount * sign),
            settled: fromCents(covered * sign),
            residual: fromCents(residual * sign),
            overdueDays: days,
        };
    });
    const residual = net - settledCents;
    const state = residual * sign <= 0
        ? 'saldata'
        : overdue > 0
            ? 'scaduta'
            : settledCents !== 0
                ? 'parziale'
                : 'aperta';
    return {
        state,
        settled: fromCents(settledCents),
        residual: fromCents(residual),
        overdue: fromCents(overdue * sign),
        nextDue,
        overdueDays,
        dues: statuses,
        difference: fromCents(net - duesTotal),
    };
}
/* --- Reading Gestify ------------------------------------------------------------ */
/**
 * The lists of Gestify read for the Tesoreria, from the first day. Each list of
 * fiscal documents answers with sales and purchases together (06/10/2026):
 * the direction comes from the type of each document, and the lists are
 * merged by id. The scadenzario likewise.
 */
export const GESTIFY_TREASURY_PATHS = {
    sales: `/api/documenti/documenti_fiscali_vendita/v1/list?data_operazione_da=${TREASURY_START_DATE}`,
    purchases: `/api/documenti/documenti_fiscali_acquisto/v1/list?data_operazione_da=${TREASURY_START_DATE}`,
    dueIn: `/api/scadenzario/entrate/v1/list?data_operazione_da=${TREASURY_START_DATE}`,
    dueOut: `/api/scadenzario/uscite/v1/list?data_operazione_da=${TREASURY_START_DATE}`,
};
const GESTIFY_MACRO = {
    emessa: 'documenti_fiscali_vendita',
    ricevuta: 'documenti_fiscali_acquisto',
};
/** The detail of one document of Gestify: split payment, withholding. */
export function gestifyItemPath(direction, id) {
    return `/api/documenti/${GESTIFY_MACRO[direction]}/v1/item/${encodeURIComponent(id)}`;
}
/** The lines of one document of Gestify. */
export function gestifyLinesPath(direction, id) {
    return `${gestifyItemPath(direction, id)}/righe/list`;
}
/**
 * The documents of Gestify that are invoices to collect or pay, by the end of
 * their type (tipo_sub «DFVE_FATT», «DFAC_NOAC_C»…): invoices, deferred,
 * accompanying, advance and simplified invoices, fees, debit and credit notes.
 */
const INVOICE_TYPES = {
    FATT: 'fattura',
    FAAC: 'fattura',
    FAAP: 'fattura',
    FAD1: 'fattura',
    FAD2: 'fattura',
    FASM: 'fattura',
    PARC: 'fattura',
    PAAC: 'fattura',
    NODE: 'fattura',
    NOAC: 'nota_credito',
};
/** Why the other documents stay out, as the preview counts them. */
function excludedLabel(type) {
    if (type.startsWith('ES'))
        return 'esterometro';
    if (type === 'STIP')
        return 'stipendi';
    if (type === 'TASS')
        return 'tasse';
    if (type === 'SCRI')
        return 'scritture';
    return `altri documenti (${type || 'senza tipo'})`;
}
/**
 * The self-billing and reverse-charge integrations of the electronic invoice:
 * kept as «autofatture» (07/10/2026), out of the totals unless asked for.
 */
const INTEGRATION_ADE = new Set([
    'TD16',
    'TD17',
    'TD18',
    'TD19',
    'TD20',
    'TD21',
    'TD22',
    'TD23',
    'TD27',
    'TD28',
    'TD29',
]);
/** Direction and kind of a document of Gestify, or why it is not an invoice of the Tesoreria. */
export function gestifyDocumentType(tipoSub, tipoAde) {
    const [prefix = '', type = ''] = (tipoSub ?? '').toUpperCase().split('_');
    const direction = prefix === 'DFVE' ? 'emessa' : prefix === 'DFAC' ? 'ricevuta' : null;
    if (!direction)
        return { excluded: `altri documenti (${tipoSub || 'senza tipo'})` };
    // Self-billing: the AU types of Gestify, or an integration code of the electronic invoice.
    if (type.startsWith('AU') || INTEGRATION_ADE.has((tipoAde ?? '').toUpperCase())) {
        return { direction, kind: type === 'NOAC' ? 'nota_credito' : 'fattura', selfBilling: true };
    }
    const kind = INVOICE_TYPES[type];
    return kind ? { direction, kind, selfBilling: false } : { excluded: excludedLabel(type) };
}
/**
 * A self-billing is paid for its taxable (3.17): the VAT is due and deductible
 * together, the foreign supplier never sees it. Its instalment follows.
 */
export function selfBillingDue(invoice) {
    if (!invoice.selfBilling || !invoice.dues.length)
        return invoice;
    return {
        ...invoice,
        dues: [{ dueDate: invoice.dues[0].dueDate, amount: invoice.taxable }],
    };
}
const money = z
    .string()
    .regex(/^-?\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value)));
const decimal = z.string().regex(/^-?\d{1,12}(\.\d{1,4})?$/, { message: 'Numero non valido' });
export const invoiceLineSchema = z.object({
    description: z.string().max(4000),
    quantity: decimal.nullable(),
    unitPrice: decimal.nullable(),
    discount: z.string().max(40).nullable(),
    vat: z.string().max(100).nullable(),
    amount: money,
});
/** What only the detail of a document says: read for new and changed invoices. */
export const invoiceDetailsSchema = z.object({
    splitPayment: z.boolean(),
    withholding: money,
    lines: z.array(invoiceLineSchema).max(1000),
});
export const importedInvoiceSchema = z.object({
    gestifyId: z.string().trim().min(1).max(40),
    direction: z.enum(INVOICE_DIRECTIONS),
    kind: z.enum(GESTIFY_INVOICE_KINDS),
    /** Self-billing or reverse-charge integration: out of the totals unless asked for. */
    selfBilling: z.boolean().default(false),
    number: z.string().trim().min(1).max(60),
    date: isoDateSchema,
    counterparty: z.object({
        gestifyId: z.string().trim().max(40).nullable(),
        name: z.string().trim().min(1).max(200),
        vatNumber: z.string().trim().max(30).nullable(),
        taxCode: z.string().trim().max(30).nullable(),
    }),
    taxable: money,
    vat: money,
    total: money,
    paymentTerms: z.string().trim().max(200).nullable(),
    dues: z.array(z.object({ dueDate: isoDateSchema, amount: money })).max(120),
    /**
     * The withholding tax as the scadenzario of Gestify has it (its «ritenuta
     * d'acconto» due dates, 07/10/2026); missing when it has none, and then the
     * detail says it.
     */
    withholding: money.optional(),
    /** Missing when the invoice did not change and its detail was already read. */
    details: invoiceDetailsSchema.optional(),
});
export const importInvoicesSchema = z.object({
    /** Every invoice of Gestify since the first day: those missing here are removed. */
    invoices: z.array(importedInvoiceSchema).max(20_000),
    /** True for the preview: nothing is written. */
    dryRun: z.boolean().default(false),
});
/** The key of an invoice of Gestify in K-Management. */
export function invoiceKey(invoice) {
    return `${invoice.direction}:${invoice.gestifyId}`;
}
/** The fields of a record whatever their name in Gestify: the first one present. */
function pick(record, names) {
    for (const name of names) {
        const value = record[name];
        if (value !== undefined && value !== null && value !== '')
            return value;
    }
    return undefined;
}
function textOf(value) {
    if (value === undefined || value === null)
        return null;
    if (typeof value === 'object') {
        const item = value;
        return textOf(item.descrizione) ?? textOf(item.id);
    }
    const text = String(value).trim();
    return text === '' ? null : text;
}
function idOf(value) {
    if (value !== null && typeof value === 'object')
        return textOf(value.id);
    return textOf(value);
}
/** An amount of Gestify (number, or text with a dot or an Italian comma) in cents, 0 when missing. */
function centsOf(value) {
    if (typeof value === 'number' && Number.isFinite(value))
        return Math.round(value * 100);
    const text = textOf(value);
    if (!text)
        return 0;
    const normal = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
    const number = Number(normal);
    return Number.isFinite(number) ? Math.round(number * 100) : 0;
}
/** A date of Gestify (ISO, with or without time, or dd/mm/yyyy) as YYYY-MM-DD. */
function dateOf(value) {
    const text = textOf(value);
    if (!text)
        return null;
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (iso)
        return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const italian = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text);
    if (italian)
        return `${italian[3]}-${italian[2].padStart(2, '0')}-${italian[1].padStart(2, '0')}`;
    return null;
}
function flagOf(value) {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value !== 0;
    const text = textOf(value)?.toLowerCase();
    return text === 'true' || text === 's' || text === 'si' || text === 'sì' || text === '1';
}
/**
 * A document of Gestify as K-Management keeps it, from the lists. The fields
 * are those of the API (checked on the first real reading, 06/10/2026); a
 * record that cannot be read comes back with the names of its fields (never
 * the values), to fix the reading. Documents that are not invoices to collect
 * or pay (self-billing, salaries, taxes…) come back as excluded.
 */
export function gestifyInvoice(record) {
    const fail = (reason) => ({
        ok: false,
        reason,
        fields: Object.keys(record).sort(),
    });
    const type = gestifyDocumentType(textOf(record.tipo_sub), textOf(record.tipo_ade));
    if ('excluded' in type)
        return { ok: false, excluded: type.excluded };
    const { direction, kind, selfBilling } = type;
    const id = idOf(pick(record, ['id', 'documento_id']));
    if (!id)
        return fail('senza identificativo');
    const number = textOf(direction === 'ricevuta'
        ? pick(record, ['numero_documento_fornitore', 'numero_documento', 'numero'])
        : pick(record, ['numero_documento', 'numero']));
    if (!number)
        return fail('senza numero');
    const date = dateOf(pick(record, ['data_documento', 'data_operazione', 'data']));
    if (!date)
        return fail('senza data');
    const party = pick(record, ['anagrafica']);
    const partyRecord = party !== null && typeof party === 'object' ? party : {};
    const name = textOf(pick(record, ['sede_legale_ragione_sociale', 'ragione_sociale']) ??
        pick(partyRecord, ['ragione_sociale', 'descrizione']));
    if (!name)
        return fail('senza cliente o fornitore');
    const totalCents = centsOf(pick(record, ['totale_documento_ivato', 'totale_documento']));
    const vatCents = centsOf(pick(record, ['totale_documento_iva_importo', 'totale_iva']));
    // Credit notes reduce what is due: negative amounts, whatever sign Gestify uses.
    const signed = (cents) => fromCents(kind === 'nota_credito' ? -Math.abs(cents) : cents);
    return {
        ok: true,
        invoice: {
            gestifyId: id,
            direction,
            kind,
            selfBilling,
            number,
            date,
            counterparty: {
                gestifyId: idOf(pick(record, ['anagrafica_id']) ?? partyRecord.id),
                name: name.slice(0, 200),
                vatNumber: textOf(pick(record, ['partita_iva']) ?? partyRecord.partita_iva),
                taxCode: textOf(pick(record, ['codice_fiscale']) ?? partyRecord.codice_fiscale),
            },
            taxable: signed(totalCents - vatCents),
            vat: signed(vatCents),
            total: signed(totalCents),
            paymentTerms: textOf(pick(record, ['pagamento_termini']))?.slice(0, 200) ?? null,
        },
    };
}
/** A number of Gestify as a decimal string with at most `decimals` digits, or null. */
function decimalOf(value, decimals) {
    if (value === undefined || value === null || value === '')
        return null;
    const number = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
    if (!Number.isFinite(number))
        return null;
    return String(Number(number.toFixed(decimals)));
}
/**
 * The detail of a document of Gestify: split payment and withholding, which
 * the lists do not carry, and its lines, read-only. The withholding here is
 * its rate on the taxable (which includes the social security contribution):
 * used only when the scadenzario has no «ritenuta d'acconto» for the invoice.
 * The «totale netto» of Gestify is the taxable, not the net to pay.
 */
export function gestifyInvoiceDetails(item, lines, invoice) {
    const splitPayment = flagOf(item.split_payment_attivo);
    const taxable = Math.abs(toCents(invoice.total)) - Math.abs(toCents(invoice.vat));
    const rate = Number(item.ritenuta_percentuale ?? 0);
    const withheld = flagOf(item.ritenuta_attiva) && Number.isFinite(rate) && rate > 0
        ? Math.round((taxable * rate) / 100)
        : 0;
    const credit = invoice.kind === 'nota_credito';
    const purchase = invoice.direction === 'ricevuta';
    const priced = (line, sale, buy) => purchase ? (pick(line, [buy]) ?? pick(line, [sale])) : pick(line, [sale]);
    return {
        splitPayment,
        withholding: fromCents(credit ? -withheld : withheld),
        lines: [...lines]
            .sort((a, b) => Number(a.posizione ?? 0) - Number(b.posizione ?? 0))
            .map((line) => {
            const parts = [textOf(line.descrizione), textOf(line.descrizione_estesa)];
            const description = parts
                .filter((part, index) => Boolean(part) && parts.indexOf(part) === index)
                .join('\n');
            const discount = [line.sconto_1, line.sconto_2]
                .map((value) => decimalOf(value, 2))
                .filter((value) => value !== null && Number(value) !== 0)
                .join('+');
            return {
                description: description.slice(0, 4000),
                quantity: decimalOf(line.quantita, 3),
                unitPrice: decimalOf(priced(line, 'prezzo_vendita_netto', 'prezzo_acquisto_netto'), 4),
                discount: discount || null,
                vat: textOf(line.iva)?.slice(0, 100) ?? null,
                amount: fromCents(centsOf(priced(line, 'importo_vendita_netto', 'importo_acquisto_netto'))),
            };
        }),
    };
}
/**
 * The withholding tax among the due dates: type «RA» (as a code or as an item
 * {id, descrizione}), a type or payment method that speaks of «ritenuta», or
 * the payment method «quietanza erario».
 */
function isWithholdingDue(record) {
    const type = record.tipo;
    const code = (idOf(type) ?? '').toUpperCase();
    const words = [textOf(type), textOf(record.pagamento_metodo)].join(' ').toLowerCase();
    return code === 'RA' || words.includes('ritenuta') || words.includes('quietanza erario');
}
/**
 * A due date of the scadenzario of Gestify, with the invoice it belongs to.
 * Gestify lists the withholding tax of an invoice among its due dates (type
 * «RA», paid to the tax office with the F24): it is not an instalment, and
 * comes back as `withholding` (07/10/2026).
 */
export function gestifyDue(record) {
    const invoiceId = idOf(pick(record, ['documento_id', 'documento', 'testata_id', 'documento_testata_id']));
    const dueDate = dateOf(pick(record, ['data_scadenza', 'scadenza']));
    if (!invoiceId || !dueDate)
        return null;
    return {
        invoiceId,
        dueDate,
        amount: fromCents(centsOf(pick(record, ['importo', 'importo_scadenza', 'totale']))),
        withholding: isWithholdingDue(record),
    };
}
/* --- Pages and API -------------------------------------------------------------- */
export const updateInvoiceSchema = z.object({
    accountCode: z.string().trim().max(30).nullable(),
    costCenterCode: z.string().trim().max(30).nullable(),
    priorityCode: z.string().trim().max(30).nullable(),
    notes: optionalText(2000),
    /**
     * The item of the plan Base chosen for the invoice (1.44); null goes back to
     * the one of the registry, undefined leaves it as it is.
     */
    reclassCode: z.string().trim().max(12).nullable().optional(),
    /** Between linked companies (3.18); undefined leaves it as it is. */
    intercompany: z.boolean().optional(),
    /**
     * «Anche in Anagrafica» (1.74): the account, cost center, priority and item
     * chosen become those of the role of the subject, and the invoice follows it.
     */
    registry: z.boolean().default(false),
});
/** The item of the plan Base of a line (1.44): null takes the one of the invoice. */
export const lineReclassSchema = z.object({ code: z.string().trim().max(12).nullable() });
/**
 * Abbuono (1.81, decision of 15/10/2026): a residual up to this amount, a
 * difference of rounding, is closed without money by who writes the
 * Tesoreria.
 */
export const WRITE_OFF_LIMIT = '1.00';
/** Whether a residual is small enough to close with a write-off. */
export function canWriteOff(residual) {
    const cents = Math.abs(toCents(residual));
    return cents > 0 && cents <= toCents(WRITE_OFF_LIMIT);
}
/** «Chiudi tutte con abbuono»: the invoices whose small residual goes. */
export const writeOffsSchema = z.object({ ids: z.array(idSchema).min(1).max(500) });
/**
 * «Chiudi senza movimento» (2.08): the residual of an invoice, or a part of it,
 * closed without money for a reason, with notes and, if any, the document.
 */
export const CLOSURE_REASONS = ['accordo_transattivo', 'stralcio', 'rinuncia', 'altro'];
export const CLOSURE_REASON_LABELS = {
    accordo_transattivo: 'Accordo transattivo',
    stralcio: 'Credito o debito stralciato',
    rinuncia: 'Rinuncia',
    altro: 'Altro',
};
export const closureSchema = z.object({
    date: isoDateSchema,
    amount: money.refine((value) => toCents(value) !== 0, { message: 'Importo non valido' }),
    reason: z.enum(CLOSURE_REASONS, { message: 'Scegli la motivazione' }),
    note: z
        .string()
        .trim()
        .min(3, { message: 'Scrivi le note della chiusura' })
        .max(300, { message: 'Al massimo 300 caratteri' }),
});
/**
 * «Annulla la fattura» (2.15): an invoice that does not count, rejected by the
 * SDI or issued by mistake and issued again. Out of the totals, Gestify untouched.
 */
export const CANCEL_REASONS = ['scartata_sdi', 'errore', 'altro'];
export const CANCEL_REASON_LABELS = {
    scartata_sdi: 'Scartata dallo SDI',
    errore: 'Emessa per errore',
    altro: 'Altro',
};
export const cancelInvoiceSchema = z.object({
    reason: z.enum(CANCEL_REASONS, { message: 'Scegli il motivo' }),
    note: z
        .string()
        .trim()
        .min(3, { message: 'Scrivi le note dell’annullamento' })
        .max(500, { message: 'Al massimo 500 caratteri' }),
    replacedBy: idSchema.nullable().default(null),
});
/**
 * Why an amount cannot close the residual of an invoice, or null: in the
 * verse of the invoice (negative for a credit note) and not above the residual.
 */
export function closureError(residual, amount) {
    const left = toCents(residual);
    const closed = toCents(amount);
    if (left === 0)
        return 'La fattura non ha residuo da chiudere';
    if (closed === 0)
        return 'Importo non valido';
    if (Math.sign(closed) !== Math.sign(left)) {
        return 'L’importo va nello stesso verso del residuo (negativo per una nota di credito)';
    }
    if (Math.abs(closed) > Math.abs(left))
        return 'L’importo supera il residuo della fattura';
    return null;
}
export const settlementSchema = z.object({
    date: isoDateSchema,
    amount: money.refine((value) => toCents(value) !== 0, { message: 'Importo non valido' }),
    accountCode: z.string().trim().max(30).nullable(),
    note: optionalText(300),
    /** An abbuono taken from the ledgers (1.81): not a payment to link to a movement. */
    writeOff: z.boolean().default(false),
});
/**
 * The invoices a movement may pay (1.49): the items of the situation at 1
 * January only from that day on, since they were still open then.
 */
export function payableInvoices(invoices, bookingDate, openingDate) {
    return invoices.filter((invoice) => invoice.origin !== 'apertura' || !openingDate || bookingDate >= openingDate);
}
/** The electronic invoice read from Gestify, kept to show it again without Gestify. */
export const invoiceXmlSchema = z.object({
    xml: z
        .string()
        .max(FATTURA_XML_MAX_LENGTH, { message: 'File troppo grande per una fattura elettronica' })
        .refine((value) => /<(?:\w+:)?FatturaElettronica[\s>]/.test(value), {
        message: 'Non è una fattura elettronica',
    }),
});
/**
 * The storage of files of Gestify the server may download an invoice from
 * (07/10/2026): the public link of an attachment, which browsers cannot read.
 * Only https and these domains: never an address of our own network.
 */
export const GESTIFY_FILE_HOSTS = [
    'gestify.it',
    'amazonaws.com',
    'cloudfront.net',
    'blob.core.windows.net',
    'storage.googleapis.com',
    'digitaloceanspaces.com',
    'wasabisys.com',
];
export function gestifyFileHostAllowed(url) {
    try {
        const parsed = new URL(url);
        return (parsed.protocol === 'https:' &&
            GESTIFY_FILE_HOSTS.some((host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`)));
    }
    catch {
        return false;
    }
}
export const invoiceXmlLinkSchema = z.object({
    url: z.string().max(4000).refine(gestifyFileHostAllowed, {
        message: 'Link di un archivio di file non riconosciuto',
    }),
});
//# sourceMappingURL=treasury.js.map